import React, { useState } from 'react';
//Constants
import { LABELS } from '../constants/general';
import { ResumeProjectDetailValidationSchema } from '../constants/schemas';
//Utils
import { addMultipleProjectDetails } from '../middleware/projectDetails';
//Components
import Label from './Label';
//Utility Packages
import { FieldArray, Formik } from 'formik';
import { Button } from 'primereact/button';
import { Editor } from 'primereact/editor';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import CreatableSelect from 'react-select/creatable';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
//Redux
import { useSelector } from 'react-redux';
// images
import deleteIcon from '../assets/images/grid-delete-icon.svg';
import { dispatch } from '../utils/store';
import { Actions } from '../redux/actions';

const ProjectDetailForm = ({ isEditable, setIsEditable }) => {
  //Store
  const toastReff = useSelector((state) => state.notifications.toastReff);
  const { resumeData, projectDetails } = useSelector((state) => state.resumeFetchDetails);
  //State

  const initialValues = {
    projects: resumeData.Projects.map((p) => {
      const hasDuration = p?.duration?.years != null || p?.duration?.months != null;
      const years = p?.duration?.years ?? 0;
      const months = p?.duration?.months ?? 0;
      const formattedEx = hasDuration
        ? Number(`${Math.floor(Number(years))}.${Math.floor(Number(months)).toString().padStart(2, '0')}`)
        : null;
      return {
        projectTitle: p.name || null,
        duration: formattedEx,
        technology: p.technologies?.map((tech) => ({ label: tech, value: tech })) || [],
        projectDetails: p.description || null
      };
    })
  };

  const handleSubmit = (values) => {
    const formattedProjectsData = values.projects.map((project) => ({
      ...project,
      duration: Number(project.duration),
      technology: project.technology?.length
        ? project.technology.map((tech) => tech.value).toString()
        : null
    }));
    const handleSave = () => {
      dispatch(Actions.ResumeFetchDetails.SetProjectDetails, values);
      setIsEditable(true);
    };
    addMultipleProjectDetails(formattedProjectsData, handleSave, toastReff);
  };

  const handleDeleteClick = (formik, index) => {
    confirmDialog({
      header: 'Delete',
      message: 'Are you sure you want to delete?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'accept',
      accept: () => {
        const updatedProjects = formik.values.projects.filter((_, i) => i !== index);
        formik.setFieldValue('projects', updatedProjects);
      }
    });
  };

  return (
    <div className="resume-profile-details mt-3">
      <Formik
        initialValues={projectDetails ? projectDetails : initialValues}
        enableReinitialize={true}
        validationSchema={ResumeProjectDetailValidationSchema}
        onSubmit={(values) => {
          handleSubmit(values);
        }}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className="p-fluid">
            <FieldArray name="projects">
              {() => (
                <>
                  {formik.values.projects.map((_, index) => (
                    <div key={index} className="project-title">
                      <div className="details-header">
                        <h3 className="mb-3">Project {index + 1}</h3>
                        {formik.values.projects.length > 1 && !isEditable && (
                          <div>
                            <Button
                              type="button"
                              data-tooltip-id="delete-button"
                              data-tooltip-content="Delete"
                              rounded
                              outlined
                              severity="secondary"
                              aria-label="Delete"
                              onClick={() => handleDeleteClick(formik, index)}
                              className="grid-action-btn">
                              <em>
                                <img src={deleteIcon} alt="delete icon" />
                              </em>
                            </Button>
                            <ReactTooltip id="delete-button" place="top" />
                          </div>
                        )}
                      </div>
                      <ProjectForm formik={formik} index={index} isEditable={isEditable} />
                      {index < formik.values.projects.length - 1 && <hr className="mt-7 mb-5" />}
                    </div>
                  ))}
                </>
              )}
            </FieldArray>
            <div className="submit-btn-block">
              {!isEditable && (
                <Button type="submit" variant="contained">
                  {LABELS.BUTTON.SAVE}
                </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default ProjectDetailForm;

const ProjectForm = ({ formik, index, isEditable }) => {
  // State
  const [technologyInput, setTechnologyInput] = useState('');
  const [technologySuggestion, setTechnologySuggestion] = useState([]);

  const handleTechnologySuggestion = (e) => {
    const options = technologySuggestion.concat(e);
    const newOptions = options.filter(
      (item, index) => options.findIndex((obj) => obj.value === item.value) === index
    );
    setTechnologySuggestion(newOptions);
  };
  const field = (name) => `projects[${index}].${name}`;

  const getIn = (obj, path) => {
    return path
      .replace(/\[(\d+)\]/g, '.$1') // convert [0] to .0
      .split('.')
      .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  };

  const isFormFieldValid = (formik, name) =>
    !!(getIn(formik.errors, name) && getIn(formik.touched, name));

  const getFormErrorMessage = (formik, name) => {
    const error = getIn(formik.errors, name);
    const touched = getIn(formik.touched, name);
    return touched && error ? <small className="p-error">{error}</small> : null;
  };

  return (
    <div className="form-row-wrapper ">
      <div className="form-col">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('projectTitle')} text={LABELS.INPUT.PROJECT_TITLE} isBold />
            <InputText
              id={field('projectTitle')}
              name={field('projectTitle')}
              placeholder={LABELS.INPUT.PROJECT_TITLE}
              value={formik.values.projects[index].projectTitle}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('projectTitle'))
              })}
            />
          </div>
          {getFormErrorMessage(formik, field('projectTitle'))}
        </div>
      </div>
      <div className="form-col ">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('duration')} text={LABELS.INPUT.DURATION} isBold />
            <InputNumber
              id={field('duration')}
              name={field('duration')}
              placeholder={LABELS.INPUT.TOTAL_EXP_PLACEHOLDER}
              value={formik.values.projects[index].duration}
              onValueChange={(e) => {
                formik.setFieldValue(field('duration'), e.value);
                formik.setFieldTouched(field('duration'), true);
              }}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={2}
              min={0}
              max={100}
              disabled={isEditable}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('duration'))
              })}
            />
          </div>
          {getFormErrorMessage(formik, field('duration'))}
        </div>
      </div>
      <div className="form-col full-width">
        <div className="form-group-outer">
          <div className="custom-form-group autocomplete">
            <Label htmlFor={field('technology')} text={LABELS.INPUT.TECHNOLOGY} isBold />
            <CreatableSelect
              isMulti
              hideSelectedOptions={false}
              id={field('technology')}
              name={field('technology')}
              value={formik.values.projects[index].technology}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                formik.setFieldValue(field('technology'), e);
                handleTechnologySuggestion(e);
              }}
              options={technologySuggestion}
              placeholder={LABELS.INPUT.TECHNOLOGY}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('technology'))
              })}
              classNamePrefix="custom-create-select"
              inputValue={technologyInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.target.value) {
                    const newOption = { label: e.target.value, value: e.target.value };
                    const techs = [...(formik.values.projects[index].technology || []), newOption];
                    formik.setFieldValue(field('technology'), techs);
                    handleTechnologySuggestion([newOption]);
                    setTechnologyInput('');
                  }
                }
              }}
              onInputChange={(inputValue) => {
                setTechnologyInput(inputValue);
              }}
              isDisabled={isEditable}
            />
          </div>
          {getFormErrorMessage(formik, field('technology'))}
        </div>
      </div>
      <div className="form-col full-width">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor={field('projectDetails')} text={LABELS.INPUT.PROJECT_DETAILS} isBold />
            {isEditable && (
              <Editor
                id={field('projectDetails')}
                name={field('projectDetails')}
                value={formik.values.projects[index].projectDetails}
                onTextChange={(e) => formik.setFieldValue(field('projectDetails'), e.htmlValue)}
                style={{ height: '260px' }}
                readOnly={true}
                className={classNames({
                  'p-invalid': isFormFieldValid(formik, field('projectDetails'))
                })}
              />
            )}
            {!isEditable && (
              <Editor
                id={field('projectDetails')}
                name={field('projectDetails')}
                value={formik.values.projects[index].projectDetails}
                onTextChange={(e) => formik.setFieldValue(field('projectDetails'), e.htmlValue)}
                style={{ height: '260px' }}
                className={classNames({
                  'p-invalid': isFormFieldValid(formik, field('projectDetails'))
                })}
              />
            )}
          </div>
          {getFormErrorMessage(formik, field('projectDetails'))}
        </div>
      </div>
    </div>
  );
};
